import { NotificationData } from '../../../services/notification.service';
import { canOpenGateForWayPurchaseNotification } from '../../../shared/utils/gate-lane.util';
import { formatWayActiveMembershipDisplayName } from '../../../shared/utils/notification-display.util';
import { isOrderRequestLookupSource } from './way-purchase-display.helpers';
import {
  isWayPurchaseUnredeemableDetailView,
  resolveWayPurchaseDetailTitle,
  resolveWayPurchaseSelectionModalTitle,
  shouldShowWayPurchaseSelectionLayout,
} from './way-purchase-layout.util';

export const WAY_ALREADY_REDEEMED_TODAY_ERROR_CODE = 'CARWASH_ALREADY_REDEEMED_TODAY';

export function isWayPurchaseRedemptionAlreadyCompleted(
  notification: NotificationData | null | undefined
): boolean {
  const row = notification?.data as Record<string, any> | undefined;
  return row?.['redemption_completed'] === true;
}

export const WAY_PURCHASE_NOTIFICATION_TYPES = new Set<NotificationData['type']>([
  'way-single-wash',
  'way-active-subscription',
  'way-purchase-selection',
  'way-purchase-unredeemable',
  'way-subscription-already-redeemed-today',
]);

export function isWayPurchaseNotification(
  notification: NotificationData | null | undefined
): notification is NotificationData {
  return !!notification && WAY_PURCHASE_NOTIFICATION_TYPES.has(notification.type);
}

export function getWayPurchaseModalTitle(
  notification: NotificationData | null | undefined
): string {
  if (!notification) {
    return 'Way purchase';
  }

  const detailTitle = resolveWayPurchaseDetailTitle(notification);
  if (
    isWayPurchaseUnredeemableDetailView(notification) ||
    notification.type !== 'way-purchase-selection'
  ) {
    if (detailTitle !== 'Way purchase') {
      return detailTitle;
    }
  }

  if (notification.type === 'way-purchase-selection') {
    return resolveWayPurchaseSelectionModalTitle(notification);
  }

  // Match SSE titles when API reload has the event type but a thin/missing purchase snapshot.
  if (notification.type === 'way-active-subscription') {
    return formatWayActiveMembershipDisplayName(
      (notification.data ?? {}) as Record<string, any>
    );
  }
  if (notification.type === 'way-single-wash') {
    return 'Single Wash';
  }
  if (notification.type === 'way-subscription-already-redeemed-today') {
    return 'Subscription Already Redeemed';
  }
  if (notification.type === 'way-purchase-unredeemable') {
    return 'Way purchase unavailable';
  }

  return 'Way purchase';
}

export function shouldShowWayPurchaseConfirmButton(
  notification: NotificationData | null | undefined
): boolean {
  if (!notification) {
    return false;
  }
  return (
    (notification.type === 'way-single-wash' || notification.type === 'way-active-subscription') &&
    !isWayPurchaseUnredeemableDetailView(notification)
  );
}

export function getWayPurchaseConfirmButtonLabel(
  notification: NotificationData | null | undefined
): string {
  if (!notification) {
    return 'Confirm';
  }
  if (canOpenGateForWayPurchaseNotification(notification)) {
    return 'Open Gate';
  }
  if (notification.type === 'way-single-wash' || notification.type === 'way-active-subscription') {
    return 'Got it';
  }
  return 'Confirm';
}

export function getWayPurchaseConfirmLoadingLabel(
  notification: NotificationData | null | undefined
): string {
  if (!notification) {
    return 'Confirming...';
  }
  if (canOpenGateForWayPurchaseNotification(notification)) {
    return 'Opening gate...';
  }
  if (notification.type === 'way-single-wash' || notification.type === 'way-active-subscription') {
    return 'Redeeming...';
  }
  return 'Confirming...';
}

export function shouldShowWayPurchaseCancelButton(
  notification: NotificationData | null | undefined
): boolean {
  return shouldShowWayPurchaseSelectionLayout(notification);
}

export function shouldShowWayPurchaseDismissButton(
  notification: NotificationData | null | undefined
): boolean {
  return isWayPurchaseUnredeemableDetailView(notification);
}

export function getWayPurchaseDismissButtonLabel(
  notification: NotificationData | null | undefined
): string {
  if (isOrderRequestLookupSource(notification?.data as Record<string, any> | undefined)) {
    return 'Close';
  }
  return 'Continue to new order';
}
